import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ShieldAlert, XCircle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { ApiError, paymentsApi, type PaymentStatusResponse } from "@/lib/api";
import { Button } from "@/components/ui";
import { NequiBadge, NequiBolt } from "@/components/NequiBrand";
import { formatCop } from "@pahcertificar/certmanager-payment-gateway/pricing";
import Logo from "@/components/layout/Logo";

type Status = "polling" | "succeeded" | "failed" | "cancelled" | "expired" | "missing";

const POLL_INTERVAL_MS = 2000;
const SLOW_POLL_INTERVAL_MS = 10_000;
const SLOW_POLL_AFTER_MS = 3 * 60 * 1000;
const MAX_POLL_DURATION_MS = 60 * 60 * 1000;

export function PaymentReturn() {
  const { t } = useT();
  const navigate = useNavigate();
  const auth = useAuth();
  const [params] = useSearchParams();
  const { token: paramToken } = useParams<{ token?: string }>();
  const location = useLocation();
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>("polling");
  const [pollCount, setPollCount] = useState(0);
  const [pollStartedAt] = useState(() => Date.now());
  const [snapshot, setSnapshot] = useState<PaymentStatusResponse | null>(null);

  // Token resolution priority:
  //   1. URL param   (/pagar/:token        — in-app push flow)
  //   2. ?token=...  (/pagar/retorno?token= — redirect-return flow)
  const token = paramToken ?? params.get("token") ?? "";
  const queryStatus = params.get("status");

  // The Nequi phone we pushed to comes from the wizard via router state;
  // fall back to the OpenAuth phone for the redirect-return path.
  const routerState = location.state as { nequiPhone?: string; amountCop?: number } | null;
  const stateNequiPhone = routerState?.nequiPhone ?? null;
  const phone =
    stateNequiPhone ?? (auth.status === "authenticated" ? auth.user.phone : "");
  // Amount varies by certificate type + validity (PC-35): prefer the checkout
  // response relayed via router state, fall back to the polled link amount
  // (survives refreshes), else show period-free copy.
  const amountCop = routerState?.amountCop ?? snapshot?.amountCop ?? null;

  if (!token) {
    return <Navigate to="/solicitar" replace />;
  }

  useEffect(() => {
    if (queryStatus === "cancelled") {
      setStatus("cancelled");
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await paymentsApi.status(token);
        if (cancelled) return;
        setSnapshot(res);
        if (res.paid) {
          // The request already lives on the server and is flagged paid by the
          // payment-success callback — just refresh the customer's lists so the
          // dashboard + certificates pages reflect it.
          qc.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
          setStatus("succeeded");
          return;
        }
        if (res.intentStatus === "failed") {
          setStatus("failed");
          return;
        }
        if (res.intentStatus === "cancelled" || res.linkStatus === "cancelled") {
          setStatus("cancelled");
          return;
        }
        if (res.linkStatus === "expired") {
          setStatus("expired");
          return;
        }
        setPollCount((n) => n + 1);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setStatus("missing");
          return;
        }
        setPollCount((n) => n + 1);
      }
    };
    const elapsedMs = Date.now() - pollStartedAt;
    if (status === "polling" && elapsedMs < MAX_POLL_DURATION_MS) {
      const interval =
        pollCount === 0
          ? 0
          : elapsedMs < SLOW_POLL_AFTER_MS
          ? POLL_INTERVAL_MS
          : SLOW_POLL_INTERVAL_MS;
      const id = setTimeout(tick, interval);
      return () => {
        cancelled = true;
        clearTimeout(id);
      };
    }
    return () => {
      cancelled = true;
    };
  }, [token, pollCount, status, queryStatus, qc, pollStartedAt]);

  const isTakingLong = status === "polling" && Date.now() - pollStartedAt >= SLOW_POLL_AFTER_MS;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-brand-50 px-6">
      <div className="w-full max-w-sm rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-soft">
        <div className="mb-6 flex justify-center">
          <Logo />
        </div>

        {status === "polling" && (() => {
          // Nequi push: no checkoutUrl, intent stays "pending" until the
          // user approves on their phone. Show phone-centric guidance.
          // Redirect-return: user came back from a checkout page; show
          // generic "confirming with provider" copy.
          const isPush = snapshot?.checkoutUrl == null && !queryStatus;
          if (isPush) {
            return (
              <>
                <NequiBadge className="mx-auto" />
                <div
                  className="mx-auto my-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl text-white shadow-lg"
                  style={{
                    background: "linear-gradient(135deg, #FF1493 0%, #E0007F 100%)",
                    boxShadow: "0 12px 32px -12px rgba(224,0,127,0.55)",
                  }}
                >
                  <NequiBolt className="h-8 w-8" />
                </div>
                <h1 className="text-xl font-semibold text-slate-900">{t("pay.push_title")}</h1>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {t("pay.push_body")}
                </p>
                {phone && (
                  <p
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-xs"
                    style={{ background: "#FFF1F8", color: "#E0007F" }}
                  >
                    Nequi · +57 {phone.replace(/^57/, "")}
                  </p>
                )}
                <ol className="mt-6 space-y-2 text-left text-xs text-slate-600">
                  <li className="flex gap-2"><span className="font-semibold" style={{ color: "#E0007F" }}>1.</span> {t("pay.push_step1")}</li>
                  <li className="flex gap-2"><span className="font-semibold" style={{ color: "#E0007F" }}>2.</span> {t("pay.push_step2")}</li>
                  <li className="flex gap-2"><span className="font-semibold" style={{ color: "#E0007F" }}>3.</span> {amountCop !== null ? t("pay.push_step3", { amount: formatCop(amountCop) }) : t("pay.push_step3_generic")}</li>
                </ol>
                <p className="mt-6 inline-flex items-center gap-2 text-xs text-slate-500">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t("pay.push_listening")}
                </p>
              </>
            );
          }
          return (
            <>
              <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
              <h1 className="text-lg font-semibold text-slate-900">{t("pay.checking_title")}</h1>
              <p className="mt-2 text-sm text-slate-600">{t("pay.checking_body")}</p>
            </>
          );
        })()}

        {status === "succeeded" && (
          <>
            <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h1 className="text-xl font-semibold text-slate-900">{t("pay.success_title")}</h1>
            <p className="mt-2 text-sm text-slate-600">{t("pay.success_body")}</p>
            <Button onClick={() => navigate("/", { replace: true })} className="mt-6" block>
              {t("pay.go_home")}
            </Button>
          </>
        )}

        {status === "cancelled" && (
          <>
            <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
              <XCircle className="h-7 w-7" />
            </div>
            <h1 className="text-lg font-semibold text-slate-900">{t("pay.cancelled_title")}</h1>
            <p className="mt-2 text-sm text-slate-600">{t("pay.cancelled_body")}</p>
            <Button onClick={() => navigate("/solicitar", { replace: true })} className="mt-6" block>
              {t("pay.retry")}
            </Button>
          </>
        )}

        {(status === "failed" || status === "expired" || status === "missing") && (
          <>
            <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-red-100 text-red-700">
              <ShieldAlert className="h-6 w-6" />
            </div>
            <h1 className="text-lg font-semibold text-slate-900">{t("pay.failed_title")}</h1>
            <p className="mt-2 text-sm text-slate-600">{t("pay.failed_body")}</p>
            <Button onClick={() => navigate("/solicitar", { replace: true })} className="mt-6" block>
              {t("pay.retry")}
            </Button>
          </>
        )}

        {isTakingLong && (
          <p className="mt-4 text-xs text-slate-500">{t("pay.taking_too_long")}</p>
        )}
      </div>
    </div>
  );
}
